import {
  Stack,
  Group,
  CopyButton,
  ActionIcon,
  Divider,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { OrderType } from '../data-access/types/orders';
import { useOrderPage } from '../hooks/use-order-page';

import OrderStatusPill, { deriveOrderStatus } from './order-status';
import OrderTypePill from './order-type';

export const OrderDetails = () => {
  const { t } = useTranslation();
  const { order } = useOrderPage();

  return (
    <Stack gap="md">
      {/* Order ID with Copy and Status */}
      <Stack gap="xs">
        <Group align="center" justify="space-between">
          <Text c="dark" fw={600} size="lg">
            {t('common.commande')} #{order.data.id}
          </Text>
          <CopyButton timeout={2000} value={order.data.id.toString()}>
            {({ copied, copy }) => (
              <Tooltip
                label={copied ? t('common.copied') : t('common.copier-lid')}
                position="left"
                withArrow
              >
                <ActionIcon
                  color={copied ? 'pf-green' : 'gray'}
                  onClick={copy}
                  size="sm"
                  variant={copied ? 'light' : 'subtle'}
                >
                  {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        </Group>
        <OrderStatusPill size="sm" status={deriveOrderStatus(order.data)} />
      </Stack>

      <Divider />

      {/* Order Details with Icons */}
      <Stack gap="sm">
        <Group align="flex-start" gap="xs">
          <div>
            <Text c="dimmed" fw={500} size="xs" tt="uppercase">
              {t('common.periode-de-prelevement')}
            </Text>
            <Text size="sm">{order.data.sampling_period.name}</Text>
          </div>
        </Group>

        <Group align="flex-start" gap="xs">
          <div>
            <Text c="dimmed" fw={500} size="xs" tt="uppercase">
              {t('common.type-danalyse')}
            </Text>
            <OrderTypePill
              size="sm"
              type={order.data.analysis_type as OrderType}
            />
          </div>
        </Group>

        <Group align="flex-start" gap="xs">
          <div>
            <Text c="dimmed" fw={500} size="xs" tt="uppercase">
              {t('common.echantillons')}
            </Text>
            <Group gap="lg">
              <Text size="sm">
                {t('common.x-echantillons', {
                  count: order.data.nbr_samples,
                })}
              </Text>
              <Text size="sm">
                {t('common.x-sous-echantillons', {
                  count: order.data.nbr_points,
                })}
              </Text>
            </Group>
          </div>
        </Group>

        <Group align="flex-start" gap="xs">
          <div>
            <Text c="dimmed" fw={500} size="xs" tt="uppercase">
              {t('common.preleveur')}
            </Text>
            <Text size="sm">
              {order.data.sampler?.last_name} {order.data.sampler?.first_name}
            </Text>
          </div>
        </Group>

        <Group align="flex-start" gap="xs">
          <div>
            <Text c="dimmed" fw={500} size="xs" tt="uppercase">
              {t('sampling.laboratoire')}
            </Text>
            <Text size="sm">{order.data.laboratory?.name}</Text>
          </div>
        </Group>

        <Group align="flex-start" gap="xs">
          <div>
            <Text c="dimmed" fw={500} size="xs" tt="uppercase">
              {t('common.secteur-geographique')}
            </Text>
            <Text size="sm">{order.data.geosector.name}</Text>
          </div>
        </Group>

        <Group align="flex-start" gap="xs">
          <div>
            <Text c="dimmed" fw={500} size="xs" tt="uppercase">
              {t('common.nombre-de-parcelles')}
            </Text>
            <Text fw={500} size="sm">
              {order.data.nbr_fields}
            </Text>
          </div>
        </Group>
      </Stack>
    </Stack>
  );
};
